import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';
import HomeIcon from './HomeIcon';

const API = '/api/master-data';

/* ── Inline SVG Icon ── */
const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    car: 'M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v5a2 2 0 0 1-2 2h-2',
    fuel: ['M3 22V9', 'M3 9a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v13', 'M13 2l4 4', 'M17 6h1a2 2 0 0 1 2 2v3a2 2 0 0 0 2 2h0'],
    wrench: ['M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z'],
    file: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
    mapPin: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z', 'M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    truck: ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z', 'M18.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z'],
    users: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z', 'M22 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
    creditCard: ['M1 4h22v16H1z', 'M1 10h22'],
    receipt: ['M14 2H6a2 2 0 0 0-2 2v16l4-2 4 2 4-2 4 2V4a2 2 0 0 0-2-2z'],
    plus: ['M12 5v14', 'M5 12h14'],
    download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    chevronDown: 'M6 9l6 6 6-6',
    alertTriangle: ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
    building: 'M3 21h18M3 7v14m18-14v14M3 7l9-4 9 4M5 21V11m4 10V11m6 10V11m4 10V11',
};

const TABS = [
    { key: 'vehicle-types', label: 'Vehicle Types', iconKey: 'car', description: 'Configure different vehicle categories and their seating capacities.' },
    { key: 'fuel-types', label: 'Fuel Types', iconKey: 'fuel', description: 'Manage fuel variants and their current market prices.' },
    { key: 'service-types', label: 'Service Types', iconKey: 'wrench', description: 'Define maintenance and service categories for your fleet.' },
    { key: 'document-types', label: 'Document Types', iconKey: 'file', description: 'Set up required documentation types for vehicles and drivers.' },
    { key: 'trip-status', label: 'Trip Status', iconKey: 'mapPin', description: 'Manage operational states for trip lifecycle tracking.' },
    { key: 'vehicle-status', label: 'Vehicle Status', iconKey: 'truck', description: 'Define availability and maintenance states for vehicles.' },
    { key: 'driver-status', label: 'Driver Status', iconKey: 'user', description: 'Set operational status for tracking driver availability.' },
    { key: 'driver-nationalities', label: 'Driver Nationalities', iconKey: 'users', description: 'Manage the list of nationalities available for driver profiles.' },
    { key: 'driver-accommodations', label: 'Accommodation Locations', iconKey: 'building', description: 'Manage residential/accommodation locations assignable to drivers.' },
    { key: 'payment-methods', label: 'Payment Methods', iconKey: 'creditCard', description: 'Configure available payment options for billing.' },
    { key: 'expense-categories', label: 'Expense Categories', iconKey: 'receipt', description: 'Categorize operational expenses for financial reporting.' },
    { key: 'compliance-types', label: 'Compliance Types', iconKey: 'file', description: 'Manage mandatory compliance and regulatory requirements.' },
    { key: 'operator-categories', label: 'Operator Categories', iconKey: 'users', description: 'Define classification categories for your partner operators and vendors.' },
    { key: 'industries', label: 'Industries', iconKey: 'building', description: 'Manage industry verticals for operator classification.' },
    { key: 'vehicle-makes', label: 'Vehicle Makes', iconKey: 'car', description: 'Manage vehicle manufacturers.' },
    { key: 'vehicle-models', label: 'Vehicle Models', iconKey: 'car', description: 'Manage vehicle models.' },
    { key: 'seating-capacities', label: 'Seating Capacities', iconKey: 'users', description: 'Configure distinct seating capacity options.' },
    { key: 'billing-periods', label: 'Billing Periods', iconKey: 'receipt', description: 'Configure distinct billing periods for subscription price plans.' },
];

const COLUMNS = ['Name', 'Description', 'Status', 'Color / Icon', 'Actions'];

const COLOR_PALETTE = [
    '#00B2A9', '#8B5CF6', '#EC4899', '#F43F5E', '#F97316',
    '#EAB308', '#22C55E', '#10B981', '#06B6D4', '#3B82F6',
    '#2563EB', '#16539C', '#7C3AED', '#C026D3', '#DB2777',
    '#DC2626',
];

const ICON_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'car', label: 'Car' },
    { value: 'fuel', label: 'Fuel' },
    { value: 'wrench', label: 'Wrench' },
    { value: 'file', label: 'File' },
    { value: 'mapPin', label: 'Map Pin' },
    { value: 'truck', label: 'Truck' },
    { value: 'user', label: 'User' },
    { value: 'creditCard', label: 'Credit Card' },
    { value: 'receipt', label: 'Receipt' },
    { value: 'building', label: 'Building' },
];

const emptyForm = () => ({ name: '', description: '', color: '#4F6EF7', icon: '', active: true, price: 0, seatingCapacity: 0 });

/* ── Reusable Item Modal (Add + Edit) ── */
function ItemModal({ tab, initial, onClose, onSave }) {
    const isEdit = !!initial;
    const [form, setForm] = useState(initial ? { ...initial } : emptyForm());
    const singular = tab.label.endsWith('Status') ? tab.label :
        tab.label.endsWith('ies') ? tab.label.slice(0, -3) + 'y' :
            tab.label.endsWith('ses') ? tab.label.slice(0, -2) :
                tab.label.endsWith('s') ? tab.label.slice(0, -1) : tab.label;

    const handleSave = () => {
        if (!form.name.trim()) return;
        const dataToSave = { ...form };
        if (tab.key === 'fuel-types') {
            dataToSave.price = Number(dataToSave.price) || 0;
        }
        if (tab.key === 'vehicle-types') {
            dataToSave.seatingCapacity = Number(dataToSave.seatingCapacity) || 0;
        }
        onSave(dataToSave);
        onClose();
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" onClick={e => e.stopPropagation()}>
                <div className="md-modal-header" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '20px 24px' }}>
                    <div>
                        <h2 className="md-modal-title" style={{ fontSize: 18, fontWeight: 700 }}>{isEdit ? `Edit ${singular}` : `Add ${singular}`}</h2>
                        <p style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Define the properties for this master data entry.</p>
                    </div>
                    <button className="md-modal-close" onClick={onClose}>
                        <Icon d={icons.x} size={18} />
                    </button>
                </div>

                <div className="md-modal-body" style={{ padding: '24px' }}>
                    <div className="md-form-group">
                        <label className="md-label">{singular} Name<span className="md-required">*</span></label>
                        <input
                            className="md-input border-premium"
                            placeholder={`e.g. ${singular === 'Fuel Type' ? 'Diesel' : 'Premium'}`}
                            value={form.name}
                            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        />
                    </div>

                    {tab.key === 'fuel-types' && (
                        <div className="md-form-group" style={{ marginTop: 16 }}>
                            <label className="md-label">Price / Litre (or Unit)</label>
                            <input
                                type="number"
                                className="md-input border-premium"
                                placeholder="e.g. 100.00"
                                value={form.price}
                                onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                            />
                        </div>
                    )}

                    {tab.key === 'vehicle-types' && (
                        <div className="md-form-group" style={{ marginTop: 16 }}>
                            <label className="md-label">Seating Capacity</label>
                            <input
                                type="number"
                                className="md-input border-premium"
                                placeholder="e.g. 20"
                                value={form.seatingCapacity}
                                onChange={e => setForm(f => ({ ...f, seatingCapacity: e.target.value }))}
                            />
                        </div>
                    )}

                    <div className="md-form-group" style={{ marginTop: 16 }}>
                        <label className="md-label">Description</label>
                        <textarea
                            className="md-textarea border-premium"
                            placeholder="Enter a brief description of this item..."
                            rows={3}
                            value={form.description}
                            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                        />
                    </div>

                    <div className="md-form-row" style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div className="md-form-group">
                            <label className="md-label">Visual Theme</label>
                            <div className="md-color-palette" style={{ marginTop: 8 }}>
                                {COLOR_PALETTE.map(c => (
                                    <button
                                        key={c}
                                        className={`md-color-swatch${form.color === c ? ' md-color-active' : ''}`}
                                        style={{ background: c, width: 20, height: 20 }}
                                        onClick={() => setForm(f => ({ ...f, color: c }))}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="md-form-group">
                            <label className="md-label">Associated Icon</label>
                            <div className="md-select-wrap" style={{ marginTop: 8 }}>
                                <select
                                    className="md-select border-premium"
                                    value={form.icon}
                                    onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
                                    style={{ padding: '8px 12px' }}
                                >
                                    {ICON_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                                <span className="md-select-caret" style={{ right: 12 }}><Icon d={icons.chevronDown} size={14} /></span>
                            </div>
                        </div>
                    </div>

                    <div className="md-status-card premium-toggle-box" style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className="md-status-card-info">
                            <span className="md-status-card-label" style={{ fontWeight: 600, fontSize: 14 }}>Status Visibility</span>
                            <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
                                {form.active ? 'Available in forms.' : 'Currently hidden from users.'}
                            </p>
                        </div>
                        <button
                            className={`md-toggle${form.active ? ' md-toggle-on' : ''}`}
                            onClick={() => setForm(f => ({ ...f, active: !f.active }))}
                        >
                            <span className="md-toggle-thumb" />
                        </button>
                    </div>
                </div>

                <div className="md-modal-footer" style={{ borderTop: '1px solid #f1f5f9', padding: '20px 24px', background: '#f8fafc' }}>
                    <button className="md-cancel-btn" onClick={onClose} style={{ borderRadius: 12, border: '1.5px solid #e2e8f0', color: '#64748b', fontWeight: 600 }}>Discard</button>
                    <button className="premium-btn btn-primary" onClick={handleSave} style={{ minWidth: 140 }}>
                        {isEdit ? 'Update Item' : `Add ${singular}`}
                    </button>
                </div>
            </div>
        </div>
    );
}


/* ── Delete Confirm Modal ── */
function DeleteModal({ itemName, onClose, onConfirm }) {
    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal md-modal-sm" onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h2 className="md-modal-title">Delete Item</h2>
                    <button className="md-modal-close" onClick={onClose}>
                        <Icon d={icons.x} size={16} />
                    </button>
                </div>
                <div className="md-modal-body md-delete-body">
                    <div className="md-delete-icon">
                        <Icon d={icons.alertTriangle} size={28} />
                    </div>
                    <p className="md-delete-msg">
                        Are you sure you want to delete <strong>"{itemName}"</strong>?<br />
                        <span className="md-delete-sub">This action cannot be undone.</span>
                    </p>
                </div>
                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button className="md-delete-btn" onClick={() => { onConfirm(); onClose(); }}>
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ── Main Component ── */
export default function MasterData({ initialTab, operatorDetails }) {
    const [activeTab, setActiveTab] = useState(initialTab || 'vehicle-types');
    const [search, setSearch] = useState('');
    const [items, setItems] = useState({});
    const [loading, setLoading] = useState(true);

    // modal state
    const [addOpen, setAddOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);   // { tabKey, item }
    const [deleteItem, setDeleteItem] = useState(null);   // { tabKey, item }

    const [totalExpenses, setTotalExpenses] = useState(0);
    const [user, setUser] = useState(null);

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('ops-user'));
        setUser(userData);
        fetchItems();
    }, []);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await axios.get(API);
            const grouped = res.data.reduce((acc, item) => {
                const cat = item.category;
                if (!acc[cat]) acc[cat] = [];
                acc[cat].push(item);
                return acc;
            }, {});

            // Fetch Operator Categories separately from the /categories API
            try {
                const catRes = await axios.get('/api/categories');
                grouped['operator-categories'] = catRes.data.map(cat => ({
                    ...cat,
                    active: cat.isActive !== false // handle isActive vs active field naming difference
                }));
            } catch (err) {
                console.error("Fetch Operator Categories error:", err);
            }

            setItems(grouped);

            // Fetch total expenses if it's the expense category tab or just fetch it anyway for the card
            const userData = JSON.parse(localStorage.getItem('ops-user'));
            const opId = userData?.operatorId || (userData?.userType === 'operator' ? userData._id : null);
            if (opId) {
                const expRes = await axios.get(`/api/expenses/${opId}`);
                setTotalExpenses(expRes.data.totalAmount || 0);
            }
        } catch (err) {
            console.error("Fetch Master Data error:", err);
        } finally {
            setLoading(false);
        }
    };

    const currentTab = TABS.find(t => t.key === activeTab);
    const singularLabel = currentTab.label.endsWith('Status') ? currentTab.label :
        currentTab.label.endsWith('ies') ? currentTab.label.slice(0, -3) + 'y' :
            currentTab.label.endsWith('ses') ? currentTab.label.slice(0, -2) :
                currentTab.label.endsWith('s') ? currentTab.label.slice(0, -1) : currentTab.label;

    const tabItems = (items[activeTab] || []).filter(i =>
        i.name.toLowerCase().includes(search.toLowerCase())
    );

    /* ── handlers ── */
    const handleAdd = async (formData) => {
        try {
            let res;
            if (activeTab === 'operator-categories') {
                const apiRes = await axios.post('/api/categories', {
                    name: formData.name,
                    description: formData.description,
                    isActive: formData.active
                });
                res = { data: apiRes.data.category || apiRes.data };
                res.data.active = res.data.isActive !== false; // align for UI
            } else {
                res = await axios.post(API, { ...formData, category: activeTab });
            }
            setItems(prev => ({
                ...prev,
                [activeTab]: [...(prev[activeTab] || []), res.data],
            }));
        } catch (err) {
            console.error("Add Master Data error:", err);
            alert("Failed to add item");
        }
    };

    const handleEdit = async (formData) => {
        try {
            let res;
            if (activeTab === 'operator-categories') {
                const apiRes = await axios.put(`/api/categories/${editItem.item._id}`, {
                    name: formData.name,
                    description: formData.description,
                    isActive: formData.active
                });
                res = { data: apiRes.data.category || apiRes.data };
                res.data.active = res.data.isActive !== false; // align for UI
            } else {
                res = await axios.put(`${API}/${editItem.item._id}`, formData);
            }
            setItems(prev => ({
                ...prev,
                [activeTab]: (prev[activeTab] || []).map(i =>
                    i._id === editItem.item._id ? res.data : i
                ),
            }));
        } catch (err) {
            console.error("Edit Master Data error:", err);
            alert("Failed to update item");
        }
    };

    const handleDelete = async () => {
        try {
            const url = activeTab === 'operator-categories'
                ? `/api/categories/${deleteItem.item._id}`
                : `${API}/${deleteItem.item._id}`;
            await axios.delete(url);
            setItems(prev => ({
                ...prev,
                [deleteItem.tabKey]: (prev[deleteItem.tabKey] || []).filter(
                    i => i._id !== deleteItem.item._id
                ),
            }));
        } catch (err) {
            console.error("Delete Master Data error:", err);
            alert("Failed to delete item");
        }
    };

    return (
        <div className="ts-container">
            <style>{`
                /* Premium Buttons */
                .premium-btn {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 24px;
                    border-radius: 14px;
                    font-weight: 700;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: none;
                    color: white;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    white-space: nowrap;
                }
                .premium-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
                }
                .premium-btn:active {
                    transform: translateY(0);
                }
                .btn-import {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                }
                .btn-template {
                    background: linear-gradient(135deg, #00B2A9 0%, #16539C 100%);
                }
                .btn-primary {
                    background: #063A5C;
                    box-shadow: 0 4px 12px rgba(6, 58, 92, 0.25);
                }
                .premium-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }
            `}</style>
            {loading && <div className="md-loading-overlay">Loading...</div>}

            {!initialTab && (
                <aside className="ts-sidebar">
                    <div className="op-breadcrumb" style={{ margin: '0 0 20px 0', padding: 0, background: 'none' }}>
                        <HomeIcon />
                        <span className="sep">›</span>
                        <span>Settings</span>
                        <span className="sep">›</span>
                        <span className="current">Master Data</span>
                    </div>
                    <div style={{ marginBottom: 16, marginTop: 8, cursor: 'default', color: '#64748b', paddingLeft: 12 }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>System Configuration</span>
                    </div>
                    {TABS.map(tab => (
                        <div
                            key={tab.key}
                            className={`ts-nav-item ${activeTab === tab.key ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <Icon d={icons[tab.iconKey]} size={18} />
                            <span>{tab.label}</span>
                        </div>
                    ))}
                </aside>
            )}

            <main className="ts-main" style={initialTab ? { marginLeft: 0, paddingLeft: 0 } : {}}>
                {initialTab && (
                    <div className="op-breadcrumb">
                        <HomeIcon />
                        <span className="sep">›</span>
                        <span>Settings</span>
                        <span className="sep">›</span>
                        <span className="current">{currentTab.label}</span>
                    </div>
                )}
                {initialTab ? (
                    <header className="op-header" style={{ marginBottom: 24 }}>
                        <div className="op-header-left">
                            <h1 className="op-title">{currentTab.label}</h1>
                            <p className="op-subtitle">{currentTab.description}</p>
                        </div>
                    </header>
                ) : (
                    <header className="ts-header">
                        <div className="ts-title-group">
                            <h1>{currentTab.label}</h1>
                            <p>{currentTab.description}</p>
                        </div>
                    </header>
                )}

                <div className="op-actions-bar" style={{ marginBottom: 20 }}>
                    <div className="op-search-box">
                        <span className="search-icon"><Icon d={icons.search} size={14} /></span>
                        <input
                            type="text"
                            placeholder={`Search ${currentTab.label}...`}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <button className="premium-btn btn-primary" onClick={() => setAddOpen(true)}>
                        <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                        Add {singularLabel}
                    </button>
                </div>

                {/* Stats Cards Added */}
                <div className="md-stats-grid">
                    <div className="md-stat-card">
                        <div className="md-stat-icon" style={{ background: '#e6f0fa', color: '#11427D' }}>
                            <Icon d={icons[currentTab.iconKey]} size={24} />
                        </div>
                        <div className="md-stat-info">
                            <span className="md-stat-label">Total {currentTab.label}</span>
                            <h3 className="md-stat-value">{(items[activeTab] || []).length}</h3>
                        </div>
                    </div>
                    <div className="md-stat-card">
                        <div className="md-stat-icon" style={{ background: '#dcfce7', color: '#15803d' }}>
                            <Icon d={icons.plus} size={24} />
                        </div>
                        <div className="md-stat-info">
                            <span className="md-stat-label">Active {currentTab.label}</span>
                            <h3 className="md-stat-value">{(items[activeTab] || []).filter(i => i.active).length}</h3>
                        </div>
                    </div>
                    <div className="md-stat-card">
                        <div className="md-stat-icon" style={{ background: '#fee2e2', color: '#b91c1c' }}>
                            <Icon d={icons.x} size={24} />
                        </div>
                        <div className="md-stat-info">
                            <span className="md-stat-label">Inactive {currentTab.label}</span>
                            <h3 className="md-stat-value">{(items[activeTab] || []).filter(i => !i.active).length}</h3>
                        </div>
                    </div>

                    {activeTab === 'expense-categories' && (
                        <div className="md-stat-card">
                            <div className="md-stat-icon" style={{ background: '#fef3c7', color: '#92400e' }}>
                                <Icon d={icons.receipt} size={24} />
                            </div>
                            <div className="md-stat-info">
                                <span className="md-stat-label">Total Expenditure</span>
                                <h3 className="md-stat-value">{formatCurrency(totalExpenses, operatorDetails?.country)}</h3>
                            </div>
                        </div>
                    )}
                </div>

                <div className="md-panel" style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                    <div className="md-panel-toolbar" style={{ borderBottom: '1px solid #f1f5f9', background: '#fafbfc', padding: '16px 24px' }}>
                        <button className="premium-btn btn-template" style={{ padding: '8px 16px', fontSize: 13 }}>
                            <Icon d={icons.download} size={16} strokeWidth={2.5} />
                            Export CSV
                        </button>
                    </div>

                    <div className="md-table-wrap">
                        <table className="md-table">
                            <thead>
                                <tr>
                                    <th className="md-th md-th-check">
                                        <input type="checkbox" className="md-checkbox" />
                                    </th>
                                    {COLUMNS.map(col => {
                                        if (col === 'Color / Icon') {
                                            if (activeTab === 'fuel-types') return <th key="price" className="md-th">Price</th>;
                                            if (activeTab === 'vehicle-types') return <th key="capacity" className="md-th">Seating Capacity</th>;
                                        }
                                        return <th key={col} className="md-th">{col}</th>;
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {tabItems.length === 0 ? (
                                    <tr>
                                        <td colSpan={COLUMNS.length + 1} className="md-empty">
                                            <div className="md-empty-inner">
                                                <div className="md-empty-icon">
                                                    <Icon d={icons[currentTab?.iconKey || 'car']} size={32} />
                                                </div>
                                                <p className="md-empty-title">No {currentTab?.label} found</p>
                                                <p className="md-empty-sub">Click <strong>+ Add {singularLabel}</strong> to create the first entry.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    tabItems.map(item => (
                                        <tr key={item._id} className="md-tr">
                                            <td className="md-td md-th-check">
                                                <input type="checkbox" className="md-checkbox" />
                                            </td>
                                            <td className="md-td">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    {activeTab !== 'fuel-types' && activeTab !== 'vehicle-types' && (
                                                        <span className="md-color-dot" style={{ background: item.color, width: 10, height: 10, borderRadius: '50%', flexShrink: 0 }} />
                                                    )}
                                                    <strong style={{ color: '#1e293b' }}>{item.name}</strong>
                                                </div>
                                            </td>
                                            <td className="md-td" style={{ color: '#64748b', fontSize: 13 }}>{item.description || '—'}</td>
                                            <td className="md-td">
                                                <span className={`md-status-badge ${item.active ? 'md-badge-active' : 'md-badge-inactive'}`}>
                                                    {item.active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            {activeTab === 'fuel-types' && (
                                                <td className="md-td">
                                                    <span style={{ color: '#059669', fontWeight: 600, fontSize: '14px' }}>
                                                        {formatCurrency(item.price || 0, operatorDetails?.country)}
                                                    </span>
                                                </td>
                                            )}
                                            {activeTab === 'vehicle-types' && (
                                                <td className="md-td">
                                                    <span style={{ color: '#16539C', fontWeight: 600, fontSize: '14px' }}>{item.seatingCapacity || 0} Seats</span>
                                                </td>
                                            )}
                                            {activeTab !== 'fuel-types' && activeTab !== 'vehicle-types' && (
                                                <td className="md-td">
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        {item.icon ? (
                                                            <span style={{ color: item.color, display: 'flex' }}>
                                                                <Icon d={icons[item.icon]} size={16} />
                                                            </span>
                                                        ) : <span style={{ color: '#cbd5e1', fontSize: 12 }}>N/A</span>}
                                                    </div>
                                                </td>
                                            )}
                                            <td className="md-td md-actions">
                                                <button
                                                    className="md-icon-btn"
                                                    title="Edit"
                                                    onClick={() => setEditItem({ tabKey: activeTab, item })}
                                                >
                                                    <Icon d={icons.edit} size={14} />
                                                </button>
                                                <button
                                                    className="md-icon-btn md-icon-btn-danger"
                                                    title="Delete"
                                                    onClick={() => setDeleteItem({ tabKey: activeTab, item })}
                                                >
                                                    <Icon d={icons.trash} size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {/* ── Add Modal ── */}
            {addOpen && (
                <ItemModal
                    tab={currentTab}
                    initial={null}
                    onClose={() => setAddOpen(false)}
                    onSave={handleAdd}
                />
            )}

            {/* ── Edit Modal ── */}
            {editItem && (
                <ItemModal
                    tab={TABS.find(t => t.key === editItem.tabKey)}
                    initial={editItem.item}
                    onClose={() => setEditItem(null)}
                    onSave={handleEdit}
                />
            )}

            {/* ── Delete Confirm ── */}
            {deleteItem && (
                <DeleteModal
                    itemName={deleteItem.item.name}
                    onClose={() => setDeleteItem(null)}
                    onConfirm={handleDelete}
                />
            )}
        </div>
    );
}
